/* eslint-disable react/no-multi-comp */
import { useMemo } from 'react'
import { FormattedNumber, FormattedDate } from 'react-intl'
import { Text } from '@retailer-platform/shared-components/src/tds'
import { type RowInfo } from 'react-table'
import { StoreGroupsListTableEditButton } from '../components/StoreGroupsListEditButton'
import { StoreGroupsListTableDeleteButton } from '../components/StoreGroupsListDeleteButton'
import { type StoreGroup } from '../../../../../common/graphql-types/retailerLocationGroup.types'
import { Tooltip } from '@retailer-platform/shared-components/src/tds'
import { useDashMessages } from '../../../../../../intl/intl.hooks'

export interface StoreGroupsTableRowInfo extends RowInfo {
  original: StoreGroup & { bannerNames: string; bannerNamesLong: string }
}

interface useStoreGroupListTableColumns {
  onEditClicked?: (storeGroupId: string) => void
}

export const useStoreGroupListTableColumns = ({ onEditClicked }) => {
  const columnMessages = useDashMessages({
    id: 'settings.storeGroups.table.columns.id',
    name: 'settings.storeGroups.table.columns.name',
    stores: 'settings.storeGroups.table.columns.stores',
    banners: 'settings.storeGroups.table.columns.banners',
    lastEdited: 'settings.storeGroups.table.columns.lastEdited',
  })

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        Cell: ({ original: { id } }: StoreGroupsTableRowInfo) => (
          <Text typography="bodyRegular">{id}</Text>
        ),
        Header: () => <Text typography="bodyEmphasized">{columnMessages.id}</Text>,
      },
      {
        accessor: 'name',
        Cell: ({ original: { name } }: StoreGroupsTableRowInfo) => (
          <Text typography="bodyRegular">{name}</Text>
        ),
        Header: () => <Text typography="bodyEmphasized">{columnMessages.name}</Text>,
      },
      {
        Cell: ({ original: { retailerLocationIds } }: StoreGroupsTableRowInfo) => (
          <Text typography="bodyRegular">
            <FormattedNumber value={retailerLocationIds.length} />
          </Text>
        ),
        Header: () => <Text typography="bodyEmphasized">{columnMessages.stores}</Text>,
      },
      {
        Cell: ({ original: { bannerNames, bannerNamesLong } }: StoreGroupsTableRowInfo) => (
          <Tooltip label={bannerNamesLong}>
            <Text typography="bodyRegular">{bannerNames}</Text>
          </Tooltip>
        ),
        Header: () => <Text typography="bodyEmphasized">{columnMessages.banners}</Text>,
      },
      {
        Cell: ({ original: { updatedAt } }: StoreGroupsTableRowInfo) => (
          <Text typography="bodyRegular">
            <FormattedDate value={updatedAt} />
          </Text>
        ),
        Header: () => <Text typography="bodyEmphasized">{columnMessages.lastEdited}</Text>,
      },
      {
        Header: () => null,
        width: 50,
        Cell: ({ original: { id } }: StoreGroupsTableRowInfo) => (
          <StoreGroupsListTableEditButton storeGroupId={id} onEditClicked={onEditClicked} />
        ),
      },
      {
        Header: () => null,
        width: 50,
        Cell: ({ original }: StoreGroupsTableRowInfo) => (
          <StoreGroupsListTableDeleteButton storeGroup={original} />
        ),
      },
    ],
    [onEditClicked, columnMessages]
  )

  return columns
}
